import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import logo from '../../assets/img.png';
import { getChannels } from '../../api/client';
import { channelPath, environmentPath } from '../../app/routes';
import { AsyncImage } from '../../components/AsyncImage';
import { ChannelCard } from '../../components/ChannelCard';
import { ErrorState } from '../../components/ErrorState';
import { Spinner } from '../../components/Spinner';
import {
  focusGroupClassName,
  focusManager,
  focusableClassName,
} from '../../helpers/spatialFocus';
import { loadSdk, unloadSdks } from '../../lib/sdk';
import type { Channel, ItgConfig } from '../../types/app';
import { useAppSettings } from '../../hooks/useAppSettings';

export const ChannelsPage = () => {
  const { env: environment = 'dev', accountId = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isTv, device, homescreenSlug } = useAppSettings();
  const [channels, setChannels] = useState<Channel[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    Promise.all([loadSdk(environment), getChannels(accountId, environment)])
      .then(([, response]) => {
        if (!cancelled) setChannels(response.channels);
      })
      .catch((reason: unknown) => {
        if (!cancelled) {
          setError(
            reason instanceof Error
              ? reason.message
              : 'Unable to load channels',
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accountId, attempt, environment, isTv]);

  useEffect(() => {
    if (!channels?.length) return;
    const frame = window.requestAnimationFrame(() =>
      focusManager.ensureFocus(
        document.getElementById('channels-list') || undefined,
      ),
    );
    return () => window.cancelAnimationFrame(frame);
  }, [channels]);

  const goToAccounts = () => {
    unloadSdks();
    navigate(`${environmentPath(environment)}${location.search}`);
  };

  useEffect(() => {
    if (!error && channels && window.inthegame) {
      const config: ItgConfig = {
        accountId,
        channelSlug: homescreenSlug,
        device,
        env: environment,
      };

      window.inthegame.init(config);
    }

    return () => {
      try {
        // window.inthegame?.stop();
      } catch {
        // The external SDK may already be stopped during player replacement.
      }
    };
  }, [accountId, channels, device, environment, error, homescreenSlug]);

  if (error) {
    return (
      <ErrorState
        message={error}
        onRetry={() => {
          setChannels(null);
          setError(null);
          setAttempt((value) => value + 1);
        }}
      />
    );
  }

  if (!channels) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <main className="flex h-full w-full flex-col p-4">
      <header
        className={`flex h-52 shrink-0 items-center justify-center px-2 sm:h-24 sm:justify-start sm:px-4 ${focusGroupClassName}`}
      >
        <button
          type="button"
          className={`${focusableClassName} rounded-2xl p-2 outline-none focus:shadow-focus`}
          data-focus-key="channels:back"
          onClick={goToAccounts}
          aria-label="Return to accounts"
        >
          <AsyncImage
            src={logo}
            alt="In The Game"
            className="h-32 w-72 sm:h-16 sm:w-64"
          />
        </button>
      </header>

      <section
        id="channels-list"
        className={`min-h-0 flex-1 overflow-y-auto px-1 pb-6 pt-2 ${focusGroupClassName}`}
        aria-label="Channels"
      >
        {channels.length ? (
          <div className="flex flex-wrap">
            {channels.map((channel) => (
              <div key={channel.slug} className="w-full p-3 sm:w-1/2 lg:w-1/4">
                <ChannelCard
                  image={channel.image}
                  label={channel.label}
                  data-focus-key={`channel:${channel.slug}`}
                  onClick={() => {
                    window.inthegame?.stop();
                    return navigate(
                      `${channelPath(environment, accountId, channel.slug)}${location.search}`,
                      { state: { channel } },
                    );
                  }}
                />
              </div>
            ))}
          </div>
        ) : (
          <p className="p-8 text-center text-xl text-app-muted">
            This account has no channels.
          </p>
        )}
      </section>
    </main>
  );
};
